/* eslint-disable no-await-in-loop */
import allPromisesWithRetries from '@/helpers/allPromisesWithRetries'
import { log, error } from '@/services/Log'
import { BCCheckoutType } from '@/types/BigCommerce/BCCheckout'
import type { StandardAddress, StandardOrder } from '@/types/ShopFront/CheckoutStandards'
//---------------------------------------------------------------

export type OrderUpdatePendingChecker = (input: {
  order: StandardOrder
  address: Partial<StandardAddress>
}) => Promise<boolean>
export const isOrderUpdatePending: OrderUpdatePendingChecker = async ({ order, address }) => {
  log('isOrderUpdatePending', { order, address })
  const [{ isSameAddress }] = await allPromisesWithRetries(() => [import('@/helpers/addressHelpers')])
  const unassignedOptionOrChangedAddress = (
    order.shipments.filter(({
      isBopus, shippingAddress, selectedShippingOption,
    }) => (
      (!selectedShippingOption)
      || (!isBopus && (
        !isSameAddress(address, shippingAddress)
        || address?.street2 !== shippingAddress?.street2
        || address?.phone !== shippingAddress?.phone
        || address?.firstName !== shippingAddress?.firstName
        || address?.lastName !== shippingAddress?.lastName
      )
      )
    )).length > 0
  )

  const orderHasItemOutOfShipments = (
    order.cart.lineItems.filter(({ id }) => ( //  for all cart items
      order.shipments.filter(({ lineItemIds }) => ( //  search in shipments
        //  for item id in shipment
        lineItemIds.indexOf(id) >= 0
      )).length === 0 //  If no shipment is found
    )).length > 0 //  Then we have an item unassigned to a shipment
  )

  const newAddressEmail = !!(
    (address?.email && !order?.cart?.email)
    || (address?.email && order?.cart?.email && address?.email !== order?.cart?.email)
  )

  const cartTimeOut = !!(
    order?.cart?.updatedTime
    && (
      (new Date()).getTime() / 1000 - (new Date(order?.cart?.updatedTime)).getTime() / 1000
    ) > (60 * 60)
  ) // One hour

  const shouldUpdateShippingAddress = (
    unassignedOptionOrChangedAddress
    || orderHasItemOutOfShipments
    || newAddressEmail
    || cartTimeOut
  )
  log(`updateShippingAddress=> shouldUpdateShippingAddress = ${String(shouldUpdateShippingAddress)}`, {
    shouldUpdateShippingAddress,
    unassignedOptionOrChangedAddress,
    orderHasItemOutOfShipments,
    newAddressEmail,
    cartTimeOut,
  })
  return shouldUpdateShippingAddress
}

export type CheckIfAddressInvalid = (p: {
  order: { cart: { lineItems: Array<{ isFurniture: boolean }> } }
  address: { stateAcronym: string }
}) => boolean
const checkIfAddressInvalid: CheckIfAddressInvalid = ({
  order,
  address,
}) => {
  try {
    return (
      ['HI', 'AK'].includes(address.stateAcronym)
      && !!order.cart.lineItems.find((item) => !!item?.isFurniture)
    )
  } catch {
    // Fail silently
  }
  return false
}

export const updateShippingAddress = async ({
  order, address,
}: {
  order: StandardOrder, address: StandardAddress
}) => {
  /*
      3 Steps
      1 - Check if the update is necessary
      2 - Create the consignments
      3 - Update the consignments with the shipping option
    */
  // cart - for line items not yet in shipments
  // current shipments - for BOPUS, etc.
  log('updateShippingAddress START', { order, address })
  const [
    { createNonBopusConsignments },
    { standardizeOrderFromResponse },
    { areAllShipmentsAssigned },
    { recoverAvailableShippingOptions },
    { default: Axios },
  ] = await allPromisesWithRetries(() => [
    import('./createNonBopusConsignments'),
    import('@/services/Standardizers/standardizeOrderFromResponse'),
    import('@/helpers/areAllShipmentsAssigned'),
    import('./recoverAvailableShippingOptions'),
    import('axios'),
  ])
  log('updateShippingAddress Modules loaded')

  const invalidAddress = checkIfAddressInvalid({ address, order })
  if (invalidAddress) {
    log('User has put in an invalid destination', address)
    return { success: false, newOrder: order }
  }

  const shouldUpdateShippingAddress = await isOrderUpdatePending({ order, address })
  // Step 2
  if (!shouldUpdateShippingAddress) {
    log('unnecessary updateShippingAddress')
    return { success: true, newOrder: order }
  }
  let { newOrder, success } = await createNonBopusConsignments({ order, address })
  log('Update Shipping Address Step 2 Done', { newOrder, success })
  // Step 3
  const shipmentsForUpdate = (
    newOrder?.shipments //  Consignments created
    || order?.shipments //  An order with only bopus won't create new consignments
  )
  for (let i = 0; i < shipmentsForUpdate.length; i += 1) {
    // Shipment object comes from the last PUT request or form the POST request
    const shipmentId = shipmentsForUpdate[i]?.id
    let shipment = (
      newOrder?.shipments
      || shipmentsForUpdate
    ).find(({ id }) => id === shipmentId)

    // Checking for cache failure on bigCommerce
    if (!shipment?.availableShippingOptions.length) {
      log('updateShippingAddress => CACHE failure', { shipment, newOrder, order })
      const recoveredOptions = await recoverAvailableShippingOptions({ order: newOrder, shipmentId })
      if (recoveredOptions?.success) {
        shipment = recoveredOptions?.shipment
        newOrder = recoveredOptions?.newOrder
      } else {
        error('Failed to recover from cache failure', { order, newOrder, recoveredOptions })
        return { success: false }
      }
    }
    // Trying to identify a previously selected shipping option
    const originallySelectedShippingOption = order?.shipments.find(({ lineItemIds }) => (
      // Searching for a shipment that has the same lineItemIds
      lineItemIds?.length === shipment?.lineItemIds?.length
      && JSON.stringify(shipment?.lineItemIds?.sort()) === JSON.stringify(lineItemIds?.sort())
    ))?.selectedShippingOption
    const shippingOptionId = ( // Try to get originally selected option
      shipment?.availableShippingOptions.find(({ description }) => (
        description === originallySelectedShippingOption?.description
      ))?.id
      || shipment?.availableShippingOptions?.[0]?.id
    )
    if (
      shippingOptionId
      && order?.cart?.id
      && (
        !shipment?.selectedShippingOption
        || !originallySelectedShippingOption?.description
        || originallySelectedShippingOption?.description !== shipment?.selectedShippingOption?.description
      )) {
      // Updates an existing consignment. https://developer.bigcommerce.com/api-reference/storefront/checkouts/checkout-consignments/checkoutsconsignmentsbycheckoutidandconsignmentidput
      // Shipping address line item IDs or the shipping option ID can be updated using this endpoint
      // One or more of these three fields are mandatory.
      // Shipping address and line items can be updated in one request.
      const url = `/api/storefront/checkouts/${order?.cart?.id}/consignments/${shipment?.id}?include=consignments.availableShippingOptions`
      const payload = { shippingOptionId }

      try {
        const { data, status } = await Axios.put<BCCheckoutType>(url, payload)
        success = success && status === 200
        newOrder = await standardizeOrderFromResponse({ bigcResponse: data, order })
      } catch {
        error('updateShippingAddress shipping option selection FAILED', {
          url,
          payload,
          originallySelectedShippingOption,
          shipment,
          order,
          newOrder,
          shipmentIndex: i,
        })
        return { success: false }
      }
    } else {
      log('updateShippingAddress => No update for this shipment', { shipment })
    }
  }

  log('updateShippingAddress END', { success, newOrder, areAllShipmentsAssigned: areAllShipmentsAssigned(newOrder) })
  return { success, newOrder }
}
